// ========================================================================
// SocialOS SDK - Offline Queue & Sync Manager
// ========================================================================

import { OfflineOperation } from './types';
import { ErrorHandler } from './errors';
import type { StorageAdapter } from './types';
import { createStorageAdapter } from './storage';

export class OfflineQueue {
  private queue: OfflineOperation[] = [];
  private storage: StorageAdapter;
  private isSyncing: boolean = false;
  private syncInterval: number | null = null;
  private errorHandler: ErrorHandler;

  constructor(storage?: StorageAdapter) {
    this.storage = storage || createStorageAdapter();
    this.errorHandler = ErrorHandler.getInstance();
    this.loadQueue();
  }

  async add(operation: Omit<OfflineOperation, 'id' | 'timestamp' | 'retries'>): Promise<void> {
    const offlineOp: OfflineOperation = {
      ...operation,
      id: this.generateId(),
      timestamp: Date.now(),
      retries: 0
    };

    this.queue.push(offlineOp);
    await this.saveQueue();
  }

  async process(syncFn: (op: OfflineOperation) => Promise<unknown>): Promise<void> {
    if (this.isSyncing || this.queue.length === 0) {
      return;
    }

    this.isSyncing = true;

    while (this.queue.length > 0) {
      const operation = this.queue[0];

      try {
        await syncFn(operation);
        await this.remove(operation.id);
      } catch (error) {
        operation.retries++;

        if (operation.retries >= operation.maxRetries) {
          await this.remove(operation.id);
          if (operation.reject) {
            operation.reject(error);
          }
        } else {
          await this.saveQueue();
        }
      }
    }

    this.isSyncing = false;
  }

  async remove(id: string): Promise<void> {
    this.queue = this.queue.filter((op) => op.id !== id);
    await this.saveQueue();
  }

  async clear(): Promise<void> {
    this.queue = [];
    await this.saveQueue();
  }

  getQueue(): OfflineOperation[] {
    return [...this.queue];
  }

  getSize(): number {
    return this.queue.length;
  }

  startAutoSync(intervalMs: number = 30000): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = window.setInterval(() => {
      // Auto-sync will be triggered by the SDK
    }, intervalMs);
  }

  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  private async saveQueue(): Promise<void> {
    try {
      await this.storage.setItem('offline_queue', JSON.stringify(this.queue));
    } catch (error) {
      this.errorHandler.handle(error as Error);
    }
  }

  private async loadQueue(): Promise<void> {
    try {
      const data = await this.storage.getItem('offline_queue');
      if (data) {
        this.queue = JSON.parse(data) as OfflineOperation[];
      }
    } catch (error) {
      this.errorHandler.handle(error as Error);
    }
  }

  private generateId(): string {
    return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// ============ Offline Status Manager ============

export class OfflineStatusManager {
  private isOnline: boolean = navigator.onLine;
  private listeners: Array<(online: boolean) => void> = [];

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.setOnline(true));
      window.addEventListener('offline', () => this.setOnline(false));
    }
  }

  checkOnlineStatus(): boolean {
    return this.isOnline;
  }

  onStatusChange(callback: (online: boolean) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  private setOnline(online: boolean): void {
    this.isOnline = online;
    this.listeners.forEach((cb) => cb(online));
  }
}
